import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { Box, Typography, Stack, Divider, Paper, Button } from "@mui/material";
import { FoodsSelect, RoundInput, themes } from "@foodpilot/foods";
import { useApplicationParameters, appParamCacheKey } from "src/api/client-api/appParams/useApplicationParameters";
import { useUpsertAppParam } from "src/api/client-api/appParams/useUpsertAppParam";
import { useDeleteAppParam } from "src/api/client-api/appParams/useDeleteAppParam";
import { Loading } from "src/components/utils/Loading";
import { useResponsiveBreakpoints } from "src/utils/useResponsiveBreakpoints";
import { UploadImg } from "./UploadImg";
import { URLHandler } from "src/services/URLHandler";
import { SettingsPageHeader } from "../components/SettingsPageHeader";

export default function ThemePage() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { isMobile } = useResponsiveBreakpoints();
    const queryClient = useQueryClient();
    // Application parameters
    const { applicationParameters, isLoading: isLoadingParams } = useApplicationParameters();
    const upsertAppParam = useUpsertAppParam({ forceReload: false });
    const deleteAppParam = useDeleteAppParam({ forceReload: false });

    // Current values from API
    const apiTheme = applicationParameters?.theme || "foodpilot";
    const currentTabTitle = applicationParameters?.tabTitle || "";

    // Local state for immediate UI update
    const [currentTheme, setCurrentTheme] = useState(apiTheme);

    // Sync local state with API data when it changes
    useEffect(() => {
        setCurrentTheme(apiTheme);
    }, [apiTheme]);

    // Tab title state
    const [tabTitle, setTabTitle] = useState(currentTabTitle);

    // Sync local state with API data when it changes
    useEffect(() => {
        setTabTitle(currentTabTitle);
    }, [currentTabTitle]);

    const pushTabTitleUpdate = () => {
        const mutationOptions = {
            onSuccess: () => {
                queryClient.refetchQueries([appParamCacheKey]);
            },
        };
        if (tabTitle.trim() === "") {
            deleteAppParam.mutate({ key: "tabTitle" }, mutationOptions);
        } else {
            upsertAppParam.mutate(
                {
                    isNew: applicationParameters?.tabTitle === undefined,
                    key: "tabTitle",
                    value: tabTitle,
                },
                mutationOptions,
            );
        }
    };

    const handleThemeChange = (selectedOption: any) => {
        if (!selectedOption) return;

        // Update local state immediately for instant UI feedback
        setCurrentTheme(selectedOption.value);

        upsertAppParam.mutate(
            {
                isNew: applicationParameters?.theme === undefined ? true : false,
                key: "theme",
                value: selectedOption.value,
            },
            {
                onSuccess: () => {
                    // Refresh application parameters after successful mutation
                    queryClient.refetchQueries([appParamCacheKey]);
                },
            },
        );
    };

    const handleTabTitleBlur = () => {
        if (tabTitle === currentTabTitle) return;
        // Avoid a 404 when clearing an already-empty field (no param exists to delete).
        if (tabTitle.trim() === "" && applicationParameters?.tabTitle === undefined) return;
        pushTabTitleUpdate();
    };

    if (isLoadingParams) {
        return <Loading />;
    }

    // Theme options
    const themeOptions = Object.keys(themes).map((key) => ({
        value: key,
        label: key.charAt(0).toUpperCase() + key.slice(1),
    }));

    return (
        <Box sx={{ padding: isMobile ? "0.5rem" : "32px" }}>
            <SettingsPageHeader
                title={t("settings.platform.customization_languages.branding.title", "Branding")}
                subtitle={t(
                    "settings.platform.customization_languages.branding.description",
                    "Configure visual identity and platform theme assets.",
                )}
                breadcrumbs={[
                    {
                        label: t("settings.platform.title", "Platform"),
                        to: URLHandler.settings.configuration(),
                    },
                    {
                        label: t("settings.platform.customization_languages.title", "Customization & languages"),
                        to: URLHandler.settings.configurationPath("customization-languages"),
                    },
                    { label: t("settings.platform.customization_languages.branding.title", "Branding") },
                ]}
            />

            <Paper
                elevation={0}
                sx={{
                    border: "1px solid #E7E6E4",
                    borderRadius: "8px",
                    overflow: "hidden",
                    boxShadow: "none",
                }}
            >
                {/* Content */}
                <Box sx={{ padding: "32px" }}>
                    <Stack spacing="24px">
                        {/* Theme Section */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Box display="flex" flexDirection="column">
                                <Typography
                                    variant="small-medium"
                                    sx={{
                                        color: "#393A36",
                                    }}
                                >
                                    {t("theme.title", "Theme")}
                                </Typography>
                                <Typography variant="small" sx={{ color: "#767673" }}>
                                    {t("theme.description", "Select the theme of your application")}
                                </Typography>
                                <Box display="flex" flexDirection="row" paddingTop="16px">
                                    <Button variant="primary" onClick={() => navigate("/settings/custom-themes")}>
                                        {t("theme.customThemes", "Custom themes")}
                                    </Button>
                                </Box>
                            </Box>
                            <Box sx={{ width: "300px" }}>
                                <FoodsSelect
                                    selectedOption={
                                        themeOptions.find((option) => option.value === currentTheme) || null
                                    }
                                    options={themeOptions}
                                    getId={(option) => option.value}
                                    getName={(option) => option.label}
                                    onChange={handleThemeChange}
                                    textOptions={{
                                        placeholder: t("theme.selectTheme", "Select a theme"),
                                        searchPlaceholder: t("theme.searchPlaceholder", "Search..."),
                                        emptyOptionsText: t("theme.noThemeAvailable", "No theme available"),
                                        noResult: t("theme.noThemeFound", "No theme found"),
                                    }}
                                    BoxProps={{ sx: { width: "300px" } }}
                                />
                            </Box>
                        </Box>

                        <Divider sx={{ borderColor: "#E7E6E4" }} />

                        {/* Tab Title Section */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Box display="flex" flexDirection="column">
                                <Typography
                                    variant="small-medium"
                                    sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "-2px" }}
                                >
                                    {t("theme.tabTitle", "Tab title")}
                                </Typography>
                                <Typography variant="small" sx={{ lineHeight: "1.6em", color: "#767673" }}>
                                    {t("theme.tabTitleDescription", "Displayed at the top of your browser.")}
                                </Typography>
                            </Box>
                            <Box sx={{ width: "300px" }}>
                                <RoundInput
                                    value={tabTitle}
                                    onChange={(e) => setTabTitle(e.target.value)}
                                    onBlur={handleTabTitleBlur}
                                    placeholder={t("theme.tabTitlePlaceholder", "Tab title...")}
                                    sx={{
                                        width: "300px",
                                        height: "40px",
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "24px",
                                            backgroundColor: "#FFFFFF",
                                            border: "1px solid #E7E6E4",
                                            "& fieldset": {
                                                border: "none",
                                            },
                                            "& input": {
                                                padding: "0px 12px 0px 20px",
                                                fontWeight: 500,
                                                fontSize: "0.875rem",
                                                color: "#393A36",
                                            },
                                        },
                                    }}
                                />
                            </Box>
                        </Box>

                        <Divider sx={{ borderColor: "#E7E6E4" }} />

                        {/* Fav Icon Section */}

                        <UploadImg
                            title={t("theme.favIcon", "Favicon")}
                            subtitle={t(
                                "theme.favIconDescription",
                                "This icon is displayed in the browser tab as well as in the favorites.",
                            )}
                            description={t("theme.favIconSize", "Maximum size : 512x512 pixels")}
                            appParamKey="tabIcon"
                            currentFile={applicationParameters?.tabIcon}
                        />

                        <Divider sx={{ borderColor: "#E7E6E4" }} />

                        {/* Logo Section */}
                        <UploadImg
                            title={t("theme.logo", "Logo")}
                            subtitle={t("theme.logoDescription", "Main logo of the application")}
                            description={t("theme.main_logo.description", "Flexible size")}
                            appParamKey="logo"
                            currentFile={applicationParameters?.logo}
                        />

                        <Divider sx={{ borderColor: "#E7E6E4" }} />

                        {/* Logo Square Section */}
                        <UploadImg
                            title={t("theme.logoSquare", "Logo square")}
                            subtitle={t("theme.logoSquare", "Logo square")}
                            description={t(
                                "theme.logoSquareDescription",
                                "The size is FIXED in the sidebar (48×48 pixels)",
                            )}
                            appParamKey="logoSquare"
                            currentFile={applicationParameters?.logoSquare}
                        />
                    </Stack>
                </Box>
            </Paper>
        </Box>
    );
}
